// GENERATED by @platform/contracts from specs/identity/main.tsp. Do not edit; run `pnpm generate`.
/* eslint-disable */
import { z } from 'zod';
/**
 * A document a person accepts to use a product (ADR tenant-export-deletion-and-consent). Closed
 * list; a product declares the ones it asks for, with their current versions, in product.yaml.
 */
export const LegalDocumentKind = z.enum(['terms', 'privacy']);
/** A document's version as the product declares it (a date, a number): counsel's label, verbatim. */
export const DocumentVersion = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/);
/** One acceptance: this version of this document. */
export const DocumentAcceptance = z.object({
    document: LegalDocumentKind,
    version: DocumentVersion,
});
/**
 * How the person chose to receive notices about their account (a breach notice included: the FTC
 * Health Breach Notification Rule allows email only when the person chose it, 16 CFR 318.5(a)(1)).
 */
export const NoticeChannel = z.enum(['email']);
/** A later acceptance: the documents (at the versions shown) and, optionally, the notice channel. */
export const ConsentAcceptance = z.object({
    accepted: z.array(DocumentAcceptance).min(1).max(10),
    noticeChannel: NoticeChannel.optional(),
});
/** ULID-style opaque identifier for records. */
export const RecordId = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
/** Tenant identifier. The control plane's own resources use the literal `platform`. */
export const TenantId = z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/);
/** Roles as carried in the JWT `cognito:groups` claim and the memberships table. */
export const Role = z.enum(['PlatformAdmin', 'TenantAdmin', 'Manager', 'Agent']);
/** Office code inside a tenant. */
export const OfficeCode = z.string().regex(/^[A-Z0-9][A-Z0-9-]{0,31}$/);
/** Agent code inside a tenant. */
export const AgentCode = z.string().regex(/^[A-Z0-9][A-Z0-9-]{0,31}$/);
export const InvitationStatus = z.enum(['pending', 'accepted', 'revoked', 'expired']);
/** Cognito subject (the identity's stable id). */
export const Sub = z.string().min(1).max(128);
/** An invitation into this tenant. Accepting is first sign-in + set password; there is no accept route. */
export const Invitation = z.object({
    invitationId: RecordId,
    tenantId: TenantId,
    email: z.string(),
    roles: z.array(Role),
    officeCode: OfficeCode.optional(),
    agentCode: AgentCode.optional(),
    status: InvitationStatus,
    invitedBy: Sub,
    expiresAt: z.iso.datetime({ offset: true }),
    createdAt: z.iso.datetime({ offset: true }),
});
export const InvitationCreate = z.object({
    email: z.string(),
    roles: z.array(Role),
    officeCode: OfficeCode.optional(),
    agentCode: AgentCode.optional(),
});
/** Isolation tier of a tenant (SaaS Lens: pool = shared cell, silo = dedicated cell). */
export const Tier = z.enum(['pool', 'silo']);
/** One identity's membership of one tenant. An identity may hold several. */
export const Membership = z.object({
    tenantId: TenantId,
    tenantName: z.string(),
    tier: Tier,
    sub: Sub,
    roles: z.array(Role),
    officeCode: OfficeCode.optional(),
    agentCode: AgentCode.optional(),
    grants: z.array(z.string()),
    createdAt: z.iso.datetime({ offset: true }),
});
/** A document a product asks people to accept, at its current version, and where to read it. */
export const LegalDocument = z.object({
    document: LegalDocumentKind,
    version: DocumentVersion,
    url: z.string(),
});
/** The caller as the platform sees them for the current tenant. */
export const Me = z.object({
    sub: Sub,
    email: z.string(),
    displayName: z.string().optional(),
    current: Membership,
    documentsDue: z.array(LegalDocument).optional(),
    noticeChannel: NoticeChannel.optional(),
});
export const MemberStatus = z.enum(['invited', 'active', 'disabled']);
/** A membership as the tenant's admin sees it: the person and their standing. */
export const Member = z.object({
    sub: Sub,
    email: z.string(),
    displayName: z.string().optional(),
    roles: z.array(Role),
    officeCode: OfficeCode.optional(),
    agentCode: AgentCode.optional(),
    grants: z.array(z.string()),
    status: MemberStatus,
    createdAt: z.iso.datetime({ offset: true }),
});
export const MemberUpdate = z.object({
    roles: z.array(Role).optional(),
    officeCode: OfficeCode.optional(),
    agentCode: AgentCode.optional(),
    grants: z.array(z.string()).optional(),
});
/** Machine-readable error codes. Never say whether a resource exists to a caller outside its tenant. */
export const ErrorCode = z.enum([
    'validation',
    'unauthenticated',
    'forbidden',
    'notFound',
    'conflict',
    'rateLimited',
    'internal',
]);
export const ErrorDetail = z.object({ path: z.string().optional(), message: z.string() });
/** The one error shape. Sent with the matching HTTP status. */
export const ErrorResponse = z.object({
    code: ErrorCode,
    message: z.string(),
    requestId: z.string(),
    details: z.array(ErrorDetail).optional(),
});
/** One validator set per operation, keyed by operationId. Same order as the route manifest. */
export const operations = {
    Invitations_list: {
        operationId: 'Invitations_list',
        method: 'GET',
        path: '/invitations',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            status: InvitationStatus.optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Invitation),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Invitations_create: {
        operationId: 'Invitations_create',
        method: 'POST',
        path: '/invitations',
        params: z.object({}),
        query: z.object({}),
        body: InvitationCreate,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: Invitation }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Invitations_get: {
        operationId: 'Invitations_get',
        method: 'GET',
        path: '/invitations/{invitationId}',
        params: z.object({ invitationId: RecordId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Invitation }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Invitations_resend: {
        operationId: 'Invitations_resend',
        method: 'POST',
        path: '/invitations/{invitationId}/resend',
        params: z.object({ invitationId: RecordId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Invitation }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Invitations_revoke: {
        operationId: 'Invitations_revoke',
        method: 'POST',
        path: '/invitations/{invitationId}/revoke',
        params: z.object({ invitationId: RecordId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Invitation }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Self_get: {
        operationId: 'Self_get',
        method: 'GET',
        path: '/me',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Me }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Self_consent: {
        operationId: 'Self_consent',
        method: 'POST',
        path: '/me/consents',
        params: z.object({}),
        query: z.object({}),
        body: ConsentAcceptance,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Me }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Self_memberships: {
        operationId: 'Self_memberships',
        method: 'GET',
        path: '/me/memberships',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Membership),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Members_list: {
        operationId: 'Members_list',
        method: 'GET',
        path: '/members',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Member),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Members_remove: {
        operationId: 'Members_remove',
        method: 'DELETE',
        path: '/members/{sub}',
        params: z.object({ sub: Sub }),
        query: z.object({}),
        responses: {
            '204': z.undefined(),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Members_get: {
        operationId: 'Members_get',
        method: 'GET',
        path: '/members/{sub}',
        params: z.object({ sub: Sub }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Member }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Members_update: {
        operationId: 'Members_update',
        method: 'PATCH',
        path: '/members/{sub}',
        params: z.object({ sub: Sub }),
        query: z.object({}),
        body: MemberUpdate,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Member }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
};
//# sourceMappingURL=schemas.js.map